import { html, ref, when } from '@microsoft/fast-element';
import {
  completedTab,
  createIcon,
  whiteErrorIcon,
  whiteCheckIcon,
  saveIcon,
  returnBackIcon,
} from '../../../styles';
import { PROJECT_OPERATIONAL_STATUS } from '../../../utils/enums/generic.enum';
import { eventDetail } from '../../../utils/formatting';
import type { ProjectModal } from './project-modal';

export enum Labels {
  TITLE = 'New Project',
  TITLE_EDIT = 'Edit Project',
  NEW_ENTRY_TAB = 'New Entry',
  BATCH_UPLOAD_TAB = 'Batch Upload',
  CLEAR_BUTTON = 'Clear',
  ADD_BUTTON = 'New Project',
  UPLOAD_FILE_BUTTON = 'Upload File',
  CONFIRM_PROJECT_UPLOAD_TITLE = 'Confirm Project Upload',
  UPLOAD_FILE_CANCEL = 'Cancel',
  UPLOAD_FILE_CONFIRM = 'Confirm',
  PROCEED_WITH_MODELED_RESULTS = 'Proceed with Modeled Results',
  CONFIRM_SELECTED_PROJECT = 'Confirm Selected Project',
  CONFIRM_NEW_PROJECT = 'Confirm New Project',
}

export enum projectModalTabs {
  FORM = 'singleTab',
  FILE_UPLOAD = 'fileUpload',
}

export const ProjectModalTemplate = html<ProjectModal>`
  <right-side-modal
    ${ref('projectModal')}
    @modalClosing="${(x) => x.closeModal()}"
    @confirmationClosing="${(x, c) => x.confirmModal(eventDetail(c))}"
    show="${(x) => x.showModal}"
    header="${(x) => x.title}"
    confirmModalHeader="${(x) => x.confirmModalHeader}"
    confirmModalDescription="${(x) => x.confirmModalDescription}"
  >
    ${when(
      (x) => !x.isFileConfirmationState,
      html<ProjectModal>`
        ${when(
          (x) => x.isRemodelOperationalProjectSelectStage,
          html<ProjectModal>`
            <zero-button
              tabindex="-1"
              appearance="stealth"
              @click="${(x) => x.returnToProjectModalFromRemodel()}"
              class="return-button"
              slot="header-start"
            >
              ${returnBackIcon('start')}
            </zero-button>
            <div class="dialog-content">
              <remodel-operational-projects
                :project=${(x) => x.project}
                @remodelProjectSelected="${(x, c) => (x.remodelProjectId = eventDetail(c))}"
              ></remodel-operational-projects>
            </div>
          `,
        )}
        <div
          class="dialog-content"
          style="display: ${(x) => (x.isRemodelOperationalProjectSelectStage ? 'none' : '')}"
        >
          <!-- G.P. Not populating the tabs with the content as we need the form for the Edit screen without the tabs at the top! -->
          <zero-tabs
            class="top-level"
            ${ref('modalTabs')}
            style="${(x) => (x.projectModalService.modalMode === 'edit' ? 'display: none;' : '')}"
          >
            <zero-tab @click=${(x) => x.selectTab(projectModalTabs.FORM)}>
              ${(x) => Labels.NEW_ENTRY_TAB}
            </zero-tab>
            <zero-tab
              style="display: none"
              @click=${(x) => x.selectTab(projectModalTabs.FILE_UPLOAD)}
            >
              ${(x) => Labels.BATCH_UPLOAD_TAB}
            </zero-tab>
            <zero-tab-panel style="height: 0px"></zero-tab-panel>
            <zero-tab-panel class="file-upload-wrapper">
              <upload-file
                ${ref('fileUpload')}
                @fileSelected="${(x, c) => x.fileSelected(eventDetail(c))}"
              ></upload-file>
            </zero-tab-panel>
          </zero-tabs>
          <div ${ref('singleTab')} class="single-project">
            <zero-tabs ${ref('projectFieldCategories')} orientation="vertical">
              <zero-tab>
                <span
                  style="display: ${(x) => (x.generalCompleted ? 'inline' : 'none')}"
                  class="completed-tab-icon"
                >
                  ${completedTab}
                </span>
                General
              </zero-tab>
              <zero-tab>
                <span
                  style="display: ${(x) => (x.locationCompleted ? 'inline' : 'none')}"
                  class="completed-tab-icon"
                >
                  ${completedTab}
                </span>
                Location
              </zero-tab>
              <zero-tab>
                <span
                  style="display: ${(x) => (x.marketInfoCompleted ? 'inline' : 'none')}"
                  class="completed-tab-icon"
                >
                  ${completedTab}
                </span>
                Market Info
              </zero-tab>
              ${when(
                (x) => x.projectOperationalStatus !== PROJECT_OPERATIONAL_STATUS.OPERATIONAL,
                html<ProjectModal>`
                  <zero-tab>
                    <span
                      style="display: ${(x) => (x.contractsTabCompleted ? 'inline' : 'none')}"
                      class="completed-tab-icon"
                    >
                      ${completedTab}
                    </span>
                    Contract and Summaries
                  </zero-tab>
                  <zero-tab>
                    <span
                      style="display: ${(x) => (x.consultantTabCompleted ? 'inline' : 'none')}"
                      class="completed-tab-icon"
                    >
                      ${completedTab}
                    </span>
                    Consultant Reports
                  </zero-tab>
                  <zero-tab>
                    <span
                      style="display: ${(x) => (x.permitsTabCompleted ? 'inline' : 'none')}"
                      class="completed-tab-icon"
                    >
                      ${completedTab}
                    </span>
                    Permits and Government Approvals
                  </zero-tab>
                `,
              )}

              <zero-tab-panel>
                <!-- General Tab -->
                <general-tab
                  ${ref('generalTabForm')}
                  @generalTabCompleted="${(x, c) => (x.generalCompleted = eventDetail(c))}"
                  @projectOperationalStatusChanged="${(x, c) =>
                    x.updateProjectOperationalStatus(eventDetail(c))}"
                  class="tab-container"
                ></general-tab>
              </zero-tab-panel>
              <zero-tab-panel>
                <!-- Location Tab -->
                <location-tab
                  ${ref('locationTabForm')}
                  @locationTabCompleted="${(x, c) => (x.locationCompleted = eventDetail(c))}"
                  class="tab-container"
                ></location-tab>
              </zero-tab-panel>
              <zero-tab-panel>
                <!-- Market Info Tab -->
                <market-info-tab
                  ${ref('marketInfoTabForm')}
                  @marketInfoTabCompleted="${(x, c) => (x.marketInfoCompleted = eventDetail(c))}"
                  :projectOperationalStatus=${(x) => x.projectOperationalStatus}
                  class="tab-container"
                ></market-info-tab>
              </zero-tab-panel>
              <zero-tab-panel>
                <!-- Contract & Summaries Tab -->
                <contracts-tab
                  ${ref('contractTabForm')}
                  @contractsTabCompleted="${(x, c) => (x.contractsTabCompleted = eventDetail(c))}"
                  class="tab-container"
                ></contracts-tab>
              </zero-tab-panel>
              <zero-tab-panel>
                <!-- Consultant Reports Tab -->
                <consultant-tab
                  ${ref('consultantTabForm')}
                  class="tab-container"
                  @consultantTabCompleted="${(x, c) => (x.consultantTabCompleted = eventDetail(c))}"
                  class="tab-container"
                ></consultant-tab>
              </zero-tab-panel>
              <zero-tab-panel>
                <!-- Permits and Government Approvals Tab -->
                <permits-tab
                  ${ref('permitsTabForm')}
                  @permitsTabCompleted="${(x, c) => (x.permitsTabCompleted = eventDetail(c))}"
                  class="tab-container"
                ></permits-tab>
              </zero-tab-panel>
              <div slot="end">
                <div ${ref('completedChart')} id="completedChart" class="completedChart"></div>
                <p class="chart-footer">
                  You have completed ${(x) => x.data[0].y}% of the fields required to create a
                  project
                </p>
              </div>
            </zero-tabs>
          </div>
        </div>
        ${when(
          (x) => x.projectModalService.modalMode === 'create',
          html<ProjectModal>`
            ${when(
              (x) =>
                x.selectedTab === projectModalTabs.FORM &&
                !x.isRemodelOperationalProjectSelectStage,
              html<ProjectModal>`
                <zero-button
                  :display="${(x) => x.projectModalService.modalMode === 'create'}"
                  appearance="secondary"
                  slot="buttons"
                  @click="${(x) => x.clearFormFromModal()}"
                >
                  ${(x) => Labels.CLEAR_BUTTON}
                </zero-button>
                <zero-button
                  ?disabled="${(x) => !x.isFormStarted}"
                  appearance="secondary"
                  slot="buttons"
                  @click="${(x) => x.saveDraft()}"
                >
                  ${saveIcon('start')}Save as Draft
                </zero-button>
                <zero-button
                  ?disabled="${(x) => !x.isFormValid}"
                  appearance="primary"
                  slot="buttons"
                  @click="${(x) => x.submitInsert()}"
                >
                  ${createIcon('start')}Create New Project
                </zero-button>
              `,
            )}
            ${when(
              (x) => x.selectedTab === projectModalTabs.FILE_UPLOAD,
              html`
                <zero-button
                  appearance="secondary"
                  slot="buttons"
                  @click="${(x) => x.clearFileArea()}"
                  ?disabled="${(x) => !x.file}"
                >
                  ${(x) => Labels.CLEAR_BUTTON}
                </zero-button>
                <zero-button
                  appearance="primary"
                  slot="buttons"
                  @click="${(x) => x.submitFile()}"
                  ?disabled="${(x) => !x.file}"
                >
                  ${(x) => Labels.UPLOAD_FILE_BUTTON}
                </zero-button>
              `,
            )}
          `,
        )}
        ${when(
          (x) => x.projectModalService.modalMode === 'edit',
          html`
            <div
              class="update-key-metrics"
              slot="message"
              style="display: ${(x) => (x.shouldUpdateKeyMetrics ? '' : 'none')}; width:  ${(x) =>
                x.shouldUpdateKeyMetrics ? '415px' : '0'}; font-size: 13px;"
            >
              Updated Key Metrics for all Active Contracts will be requested upon submitting these
              Project edits.
            </div>
            <zero-button
              ?disabled="${(x) => !x.isFormValid}"
              appearance="primary"
              slot="buttons"
              @click="${(x) => x.editProject()}"
            >
              ${createIcon('start')}Submit
            </zero-button>
          `,
        )}
      `,
    )}
    ${when(
      (x) => x.isFileConfirmationState,
      html<ProjectModal>`
        ${when(
          (x) => x.incorrectProjectObjectsUploaded?.length > 0,
          html<ProjectModal>`
            <p class="errors-title">The following projects have errors:</p>
            <upload-validation
              :errorProjectList=${(x) => x.incorrectProjectObjectsUploaded}
            ></upload-validation>
          `,
        )}
        <reduced-upload-file
          @fileSelected="${(x, c) => x.fileSelected(eventDetail(c))}"
        ></reduced-upload-file>
        ${when(
          (x) => x.correctProjectObjectsUploaded.length > 0,
          html<ProjectModal>`
            <p class="success-title">The following projects were uploaded successfully.</p>
            <p class="confirm-text">Please confirm the projects you would like to upload:</p>
            <validation-grid
              :objectList=${(x) => x.correctProjectObjectsUploaded}
              :columnConfigs=${(x) => x.projectColumns}
            ></validation-grid>
          `,
        )}
        <zero-button
          slot="buttons"
          appearance="tertiary"
          class="button-length cancel-button"
          @click="${(x) => x.cancelFileUpload()}"
        >
          ${whiteErrorIcon('start')} ${Labels.UPLOAD_FILE_CANCEL}
        </zero-button>
        <zero-button
          slot="buttons"
          appearance="primary"
          class="button-length"
          @click="${(x) => x.insertProjectsList()}"
          ?disabled="${(x) => x.incorrectProjectObjectsUploaded?.length > 0}"
        >
          ${whiteCheckIcon('start')} ${Labels.UPLOAD_FILE_CONFIRM}
        </zero-button>
      `,
    )}
    ${when(
      (x) => x.selectedTab === projectModalTabs.FORM && x.isRemodelOperationalProjectSelectStage,
      html<ProjectModal>`
        <zero-button
          slot="buttons"
          appearance="tertiary"
          class="button-length cancel-button"
          @click="${(x) => x.proceedWithModeledResult()}"
        >
          ${Labels.PROCEED_WITH_MODELED_RESULTS}
        </zero-button>
        <zero-button
          slot="buttons"
          appearance="primary"
          class="button-length"
          @click="${(x) => x.confirmSelectedProject()}"
          ?disabled="${(x) => !x.remodelProjectId}"
        >
          ${Labels.CONFIRM_SELECTED_PROJECT}
        </zero-button>
      `,
    )}
  </right-side-modal>
`;
